/**
 * Combinar o valor depois de encontrar o adversário.
 *
 * A fila pareia por jogo, não por dinheiro. Quando duas pessoas se
 * encontram, a mesa existe mas ainda não vale nada: um propõe, o outro
 * aceita, e é só no aceite que a entrada dos dois é travada — na mesma
 * transação, no servidor. Enquanto isso não acontece, nenhum dos dois
 * perdeu um centavo, e qualquer um pode desistir.
 *
 * Por isso a tela repete o valor em todo lugar onde ele decide algo: o
 * botão de aceitar é o único ponto do fluxo em que o saldo se mexe, e o
 * jogador precisa saber exatamente quanto está aceitando.
 */
import { useEffect, useRef, useState } from 'react';

import { AnimatePresence } from 'motion/react';

import { AvatarComNome } from '../components/Avatar.tsx';
import { PartidaEncontrada } from '../components/PartidaEncontrada.tsx';
import type { GameType } from '../lib/adapter.ts';
import {
  type EstadoAposta,
  aceitarValor,
  lerAposta,
  proporValor,
  recusarValor,
} from '../lib/conta.ts';
import { STAKES, payoutCents, rakeCents } from '../lib/demo.ts';
import { GAME_LABEL, money } from '../lib/format.ts';

/** De quanto em quanto tempo o outro lado é reconsultado. */
const INTERVALO_MS = 2_000;

/** Segundos restantes até o instante, nunca negativo. */
function restam(iso: string | undefined, agora: number): number {
  if (!iso) return 0;
  const alvo = Date.parse(iso);
  if (Number.isNaN(alvo)) return 0;
  return Math.max(0, Math.ceil((alvo - agora) / 1000));
}

function Relogio({ segundos, rotulo }: { segundos: number; rotulo: string }) {
  const urgente = segundos <= 10;
  return (
    <p
      className="text-sm font-medium tabular-nums"
      aria-live="off"
      style={{ color: urgente ? 'var(--danger)' : 'var(--muted-foreground)' }}
    >
      {rotulo} {segundos}s
    </p>
  );
}

/** Como o pote fica com o valor em discussão. */
function Pote({ entrada, rakeBps }: { entrada: number; rakeBps: number }) {
  return (
    <dl className="grid gap-2 text-sm">
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">Entrada de cada um</dt>
        <dd className="money">{money(entrada)}</dd>
      </div>
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">Pote</dt>
        <dd className="money">{money(entrada * 2)}</dd>
      </div>
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">
          Taxa da plataforma ({(rakeBps / 100).toFixed(0)}%)
        </dt>
        <dd className="money">−{money(rakeCents(entrada, rakeBps))}</dd>
      </div>
      <div className="flex justify-between gap-4 border-t pt-2 font-semibold">
        <dt>Prêmio ao vencedor</dt>
        <dd className="money">{money(payoutCents(entrada, rakeBps))}</dd>
      </div>
    </dl>
  );
}

type Situacao =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'negociando'; readonly aposta: EstadoAposta }
  | { readonly tipo: 'desfeita'; readonly mensagem: string }
  | { readonly tipo: 'indisponivel' };

export function AcertarValor({
  token,
  matchId,
  gameType,
  apostaInicial,
  onAcordo,
  onDesfeita,
}: {
  token: string;
  matchId: string;
  gameType: GameType;
  apostaInicial: EstadoAposta | null;
  onAcordo: (entryFeeCents: number) => void;
  onDesfeita: () => void;
}) {
  const [situacao, setSituacao] = useState<Situacao>(
    apostaInicial ? { tipo: 'negociando', aposta: apostaInicial } : { tipo: 'carregando' },
  );
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [reais, setReais] = useState('');
  const [agora, setAgora] = useState(() => Date.now());

  // O anúncio da mesa formada só faz sentido uma vez, e só quando os dois
  // lados já têm rosto. Se a tela for reaberta depois — recarregar a
  // página no meio da negociação —, repetir o anúncio seria mentira: a
  // partida não acabou de ser encontrada.
  const [anunciando, setAnunciando] = useState(
    apostaInicial?.opponent != null && !apostaInicial.stakeAgreed,
  );

  // O acordo pode fechar pelo outro lado, e a troca de tela não pode
  // acontecer duas vezes: o WebSocket da partida seria aberto em dobro.
  const encerrado = useRef(false);

  function concluir(entryFeeCents: number): void {
    if (encerrado.current) return;
    encerrado.current = true;
    onAcordo(entryFeeCents);
  }

  /** Aplica a resposta de qualquer ação ou consulta. */
  function aplicar(resultado: Awaited<ReturnType<typeof lerAposta>>): void {
    if (resultado.estado === 'fechado') {
      concluir(resultado.entryFeeCents);
      return;
    }
    if (resultado.estado === 'desfeito') {
      setSituacao({ tipo: 'desfeita', mensagem: 'Vocês não combinaram o valor.' });
      return;
    }
    if (resultado.estado === 'recusado') {
      setErro(resultado.mensagem);
      // Mesa que não existe mais não volta: sair daqui é a única saída.
      if (resultado.codigo === 'MATCH_NOT_FOUND' || resultado.codigo === 'NOT_A_PLAYER') {
        setSituacao({ tipo: 'desfeita', mensagem: resultado.mensagem });
      }
      return;
    }
    if (resultado.estado === 'indisponivel') {
      setSituacao((atual) => (atual.tipo === 'negociando' ? atual : { tipo: 'indisponivel' }));
      return;
    }

    const aposta = resultado.aposta;
    if (aposta.stakeAgreed) {
      concluir(aposta.entryFeeCents);
      return;
    }
    if (aposta.status !== 'PENDING') {
      setSituacao({
        tipo: 'desfeita',
        mensagem: 'A mesa foi desfeita antes de vocês combinarem o valor.',
      });
      return;
    }
    setSituacao({ tipo: 'negociando', aposta });
  }

  // Consulta periódica: a proposta do outro lado chega sem aviso, e o
  // prazo corre mesmo com a tela parada.
  useEffect(() => {
    let vivo = true;
    const relogio = setInterval(() => setAgora(Date.now()), 1_000);
    const consulta = setInterval(() => {
      if (encerrado.current) return;
      void lerAposta(token, matchId).then((resultado) => {
        if (vivo) aplicar(resultado);
      });
    }, INTERVALO_MS);

    if (!apostaInicial) {
      void lerAposta(token, matchId).then((resultado) => {
        if (vivo) aplicar(resultado);
      });
    }

    return () => {
      vivo = false;
      clearInterval(relogio);
      clearInterval(consulta);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, matchId]);

  async function agir(acao: () => Promise<Awaited<ReturnType<typeof lerAposta>>>): Promise<void> {
    setEnviando(true);
    setErro(null);
    aplicar(await acao());
    setEnviando(false);
  }

  if (situacao.tipo === 'carregando') {
    return (
      <main className="grow grid place-items-center px-4 text-muted-foreground">
        Encontrando o adversário…
      </main>
    );
  }

  if (situacao.tipo === 'indisponivel' || situacao.tipo === 'desfeita') {
    return (
      <main className="grow grid place-items-center px-4">
        <div className="card card-destaque p-6 max-w-md w-full grid gap-4 text-center">
          <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
            {situacao.tipo === 'desfeita' ? 'Mesa desfeita' : 'Sem resposta do servidor'}
          </h1>
          <p className="text-muted-foreground">
            {situacao.tipo === 'desfeita'
              ? situacao.mensagem
              : 'Não deu para falar com o serviço de partidas.'}
          </p>
          <p className="text-sm text-muted-foreground">
            Nenhum valor foi travado: seu saldo continua inteiro.
          </p>
          <button type="button" className="btn btn-primary" onClick={onDesfeita}>
            Procurar outro adversário
          </button>
        </div>
      </main>
    );
  }

  const { aposta } = situacao;

  // O anúncio vem antes de qualquer coisa da negociação: é o instante em
  // que a mesa se formou, e ele perde o sentido depois de a pessoa já ter
  // visto o formulário.
  if (anunciando && aposta.opponent) {
    // `AnimatePresence` existe para o `exit` funcionar: sem ele o
    // componente sai da arvore de uma vez e a animacao de saida nunca
    // roda — o anuncio pisca e some, que e pior que nao animar nada.
    return (
      <AnimatePresence>
        <PartidaEncontrada
          key="partida-encontrada"
          eu={aposta.me}
          adversario={aposta.opponent}
          gameType={gameType}
          onFim={() => setAnunciando(false)}
        />
      </AnimatePresence>
    );
  }

  const proposta = aposta.proposal;
  const minhaVez = proposta === null || !proposta.byMe;
  const saldo = aposta.balanceCents;

  const digitado = reais.trim() === '' ? null : Math.round(Number(reais.replace(',', '.')) * 100);
  const valorDigitadoValido =
    digitado !== null &&
    Number.isFinite(digitado) &&
    Number.isInteger(digitado) &&
    digitado >= aposta.limits.minCents &&
    digitado <= aposta.limits.maxCents &&
    digitado <= saldo;

  const segundosProposta = restam(proposta?.deadlineAt, agora);
  const segundosMesa = restam(aposta.negotiationEndsAt, agora);

  async function propor(valor: number): Promise<void> {
    await agir(() => proporValor(token, matchId, valor));
    setReais('');
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8 grid gap-6 lg:grid-cols-[1fr_20rem] items-start content-start grow">
      <section className="grid gap-4 content-start">
        <div className="grid gap-4">
          <div>
            <span className="selo">adversário encontrado</span>
            <h1 className="mt-3 font-bold" style={{ fontSize: 'var(--text-title)' }}>
              Combinem o valor
            </h1>
          </div>

          {/* O adversário fica à vista durante a negociação inteira. O
              anúncio passa em dois segundos, e é durante este formulário
              que a pessoa decide quanto arriscar contra quem. */}
          <div className="card p-4">
            <AvatarComNome
              avatar={aposta.opponent?.avatar}
              cor={aposta.opponent?.avatarColor}
              nome={aposta.opponent?.displayName ?? 'Seu adversário'}
              detalhe={`${GAME_LABEL[gameType]} — o valor só sai do saldo quando os dois concordarem`}
            />
          </div>
        </div>

        {proposta && !proposta.byMe ? (
          <div className="card card-destaque p-5 grid gap-3">
            <p className="rotulo">O adversário propôs</p>
            <p className="money font-bold" style={{ fontSize: 'var(--text-display)' }}>
              {money(proposta.entryFeeCents)}
            </p>
            <Relogio segundos={segundosProposta} rotulo="Vence em" />
            {proposta.entryFeeCents > saldo ? (
              <p className="text-sm font-medium" style={{ color: 'var(--danger)' }}>
                Seu saldo não cobre esse valor. Proponha outro.
              </p>
            ) : null}

            {/* O aceite fica colado no valor: é o único botão da tela que
                mexe em saldo, e o relógio corre enquanto ele estiver
                fora de vista. No celular, empurrado para o fim da página,
                ele obrigaria a rolar contra o cronômetro. */}
            <button
              type="button"
              className="btn btn-primary !py-3"
              // Zerado o relógio local, o botão sai do ar antes de o
              // servidor confirmar: melhor recusar cedo do que deixar
              // clicar num aceite que já não vale.
              disabled={
                enviando ||
                proposta.expired ||
                segundosProposta === 0 ||
                proposta.entryFeeCents > saldo
              }
              onClick={() => void agir(() => aceitarValor(token, matchId))}
            >
              {enviando ? 'Um instante…' : `Aceitar ${money(proposta.entryFeeCents)}`}
            </button>
          </div>
        ) : null}

        {proposta && proposta.byMe ? (
          <div className="card p-5 grid gap-3">
            <p className="rotulo">Você propôs</p>
            <p className="money font-bold" style={{ fontSize: 'var(--text-title)' }}>
              {money(proposta.entryFeeCents)}
            </p>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              Esperando a resposta do adversário.
            </p>
            <Relogio segundos={segundosProposta} rotulo="Vence em" />
          </div>
        ) : null}

        <h2 className="mt-2 rotulo">
          {proposta === null
            ? 'Proponha um valor'
            : proposta.byMe
              ? 'Mudar sua proposta'
              : 'Ou proponha outro valor'}
        </h2>

        <ul className="flex flex-wrap gap-2" role="group" aria-label="Valores sugeridos">
          {STAKES.map((stake) => (
            <li key={stake.cents}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={enviando || stake.cents > saldo || stake.cents > aposta.limits.maxCents}
                onClick={() => void propor(stake.cents)}
              >
                {stake.rotulo}
              </button>
            </li>
          ))}
        </ul>

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (valorDigitadoValido && digitado !== null) void propor(digitado);
          }}
        >
          <label className="grid gap-1.5">
            <span className="rotulo">Outro valor (R$)</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min={aposta.limits.minCents / 100}
              max={Math.min(aposta.limits.maxCents, saldo) / 100}
              value={reais}
              onChange={(e) => setReais(e.target.value)}
              placeholder={(aposta.limits.minCents / 100).toFixed(2)}
              className="w-40 rounded-lg border px-4 py-2.5 outline-none transition"
              style={{ background: 'var(--card)' }}
            />
          </label>
          <button
            type="submit"
            className="btn btn-ghost"
            disabled={enviando || !valorDigitadoValido}
          >
            Propor
          </button>
        </form>

        <p className="text-xs text-muted-foreground">
          Seu saldo: <span className="money">{money(saldo)}</span>. Entre{' '}
          {money(aposta.limits.minCents)} e {money(aposta.limits.maxCents)}.
        </p>
      </section>

      <aside className="card card-destaque p-5 grid gap-4 content-start">
        <h2 className="rotulo">Como fica o pote</h2>

        {proposta ? (
          <Pote entrada={proposta.entryFeeCents} rakeBps={proposta.rakeBps} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Assim que alguém propuser um valor, a divisão do pote aparece aqui.
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          A entrada dos dois é travada no mesmo instante em que o valor é aceito. Empate devolve
          integral, sem taxa.
        </p>

        <Relogio segundos={segundosMesa} rotulo="A mesa se desfaz em" />

        <button
          type="button"
          className="btn btn-ghost"
          disabled={enviando}
          onClick={() => void agir(() => recusarValor(token, matchId))}
        >
          Desistir desta mesa
        </button>

        {erro ? (
          <p role="alert" className="text-sm font-medium" style={{ color: 'var(--danger)' }}>
            {erro}
          </p>
        ) : null}

        {!minhaVez && !erro ? (
          <p className="sr-only" aria-live="polite">
            Proposta enviada, esperando resposta.
          </p>
        ) : null}
      </aside>
    </main>
  );
}
